import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Icon } from "./components/icons";
import { ApiError, api, type Me } from "./lib/api";
import { Analytics } from "./pages/Analytics";
import { Audiences } from "./pages/Audiences";
import { Autopilot } from "./pages/Autopilot";
import { BlastNew } from "./pages/BlastNew";
import { Blasts } from "./pages/Blasts";
import { CampaignNew } from "./pages/CampaignNew";
import { Campaigns } from "./pages/Campaigns";
import { ContactDetail } from "./pages/ContactDetail";
import { Cohorts } from "./pages/Cohorts";
import { Dialing } from "./pages/Dialing";
import { Contacts } from "./pages/Contacts";
import { Copy } from "./pages/Copy";
import { Fields } from "./pages/Fields";
import { Import } from "./pages/Import";
import { Journeys } from "./pages/Journeys";
import { Keys } from "./pages/Keys";
import { Activity } from "./pages/Activity";
import { Health } from "./pages/Health";
import { Redialing } from "./pages/Redialing";
import { RedialTrafficPage } from "./pages/RedialTraffic";
import { Reminders } from "./pages/Reminders";
import { Queue } from "./pages/Queue";
import { Replies } from "./pages/Replies";
import { Settings } from "./pages/Settings";
import { Today } from "./pages/Today";
import { TrafficPage } from "./pages/Traffic";

type NavItem = {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  roles?: string[];
  /** Shown only when this says so, whatever the role. */
  only?: (me: Me) => boolean;
};

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: "Work",
    items: [
      { to: "/", label: "Today", icon: "today", end: true },
      { to: "/queue", label: "Waiting on you", icon: "inbox" },
      { to: "/replies", label: "Unanswered", icon: "reply" },
      { to: "/contacts", label: "Contacts", icon: "contacts" },
    ],
  },
  {
    group: "Sending",
    items: [
      { to: "/audiences", label: "Audiences", icon: "audiences", roles: ["admin", "manager", "analyst", "autopilot"] },
      { to: "/copy", label: "Messages", icon: "messages", roles: ["admin", "manager"] },
      { to: "/blasts", label: "Blasts", icon: "blasts", roles: ["admin", "manager"] },
      { to: "/campaigns", label: "Campaigns", icon: "campaigns" },
      { to: "/reminders", label: "Reminders", icon: "reminders" },
      { to: "/traffic", label: "Traffic", icon: "traffic", roles: ["admin", "manager", "analyst", "autopilot"] },
      { to: "/autopilot", label: "Autopilot", icon: "autopilot", roles: ["admin", "manager", "analyst", "autopilot"] },
      { to: "/journeys", label: "Journeys", icon: "journeys", roles: ["admin", "manager"] },
    ],
  },
  {
    group: "Insight",
    items: [
      { to: "/analytics", label: "Analytics", icon: "analytics" },
      { to: "/cohorts", label: "Cohorts", icon: "cohorts" },
      // Under Insight rather than Admin: this is a performance report, not an operational
      // control. The /redialing screen is the control surface and stays where it is.
      { to: "/dialing", label: "Dialing", icon: "analytics",
        roles: ["admin", "manager", "analyst"] },
    ],
  },
  {
    group: "Admin",
    items: [
      { to: "/import", label: "Import contacts", icon: "import", roles: ["admin", "manager"] },
      { to: "/fields", label: "Contact fields", icon: "fields", roles: ["admin", "manager"] },
      { to: "/settings", label: "Settings", icon: "settings", roles: ["admin", "manager"] },
      { to: "/health", label: "System health", icon: "health", roles: ["admin", "manager", "analyst", "autopilot"] },
      // Under Admin rather than beside the campaigns: this screen is about the dialer,
      // not about messaging, and its one actionable number (removal unverified) is an
      // operational fault rather than a marketing question.
      { to: "/redial-traffic", label: "Redial traffic", icon: "traffic", roles: ["admin", "manager", "analyst"] },
      { to: "/redialing", label: "Redialing", icon: "health", roles: ["admin", "manager", "analyst"] },
      { to: "/activity", label: "Activity", icon: "activity", only: (me) => !!me.can_see_activity },
      { to: "/keys", label: "API keys", icon: "key", only: (me) => !!me.can_manage_keys },
    ],
  },
];

/** The page's name and its section, for the top bar. */
function pageFor(path: string): { label: string; group?: string } {
  const extra: Record<string, { label: string; group: string }> = {
    "/blasts/new": { label: "New blast", group: "Sending" },
    "/campaigns/new": { label: "New campaign", group: "Sending" },
  };
  if (extra[path]) return extra[path];
  if (path.startsWith("/campaigns/") && path.endsWith("/edit")) return { label: "Edit campaign", group: "Sending" };
  if (path.startsWith("/contacts/")) return { label: "Contact", group: "Work" };
  for (const section of NAV) {
    for (const item of section.items) {
      if (item.to === path) return { label: item.label, group: section.group };
    }
  }
  return { label: "Waterfall" };
}

function useTheme() {
  const [dark, setDark] = useState(
    () => document.documentElement.dataset.theme === "dark",
  );
  const toggle = () => {
    const explicit = document.documentElement.dataset.theme;
    const isDark = explicit
      ? explicit === "dark"
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.dataset.theme = isDark ? "light" : "dark";
    setDark(!isDark);
  };
  return { dark, toggle };
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((e) => {
        // a 401 already redirects to sign-in inside the client
        if (!(e instanceof ApiError && e.status === 401)) setError(String(e.message));
      });
  }, []);

  if (error) {
    return (
      <div className="center">
        <div className="card" style={{ maxWidth: 460 }}>
          <div className="cb col">
            <h2>Could not reach the console</h2>
            <p className="mut">{error}</p>
            <button className="btn" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="center">
        <div className="mut">Signing in…</div>
      </div>
    );
  }

  const here = pageFor(location.pathname);
  const initials = (me.name ?? me.email)
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <div className="app">
      <aside className="rail">
        <div className="brand">
          <i>W</i>
          <div>
            <b>Waterfall</b>
            <span>Vaunt · sending console</span>
          </div>
        </div>

        {NAV.map((section) => {
          const visible = section.items.filter(
            (i) =>
              (i.only ? i.only(me) : true) &&
              (!i.roles || i.roles.includes(me.role) || me.role === "admin"),
          );
          if (!visible.length) return null;
          return (
            <div key={section.group}>
              <div className="navgroup">{section.group}</div>
              <nav className="nav">
                {visible.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) => (isActive ? "on" : "")}
                  >
                    <Icon name={n.icon} />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          );
        })}

        <div className="railfoot">
          <div className="av">{initials}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <b className="who">{me.name ?? me.email}</b>
            <span className="role">{me.role}</span>
          </div>
          <button
            className="railbtn"
            title="Sign out"
            aria-label="Sign out"
            onClick={async () => {
              await api.logout();
              navigate("/");
              window.location.reload();
            }}
          >
            <Icon name="logout" size={16} />
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="top">
          <div className="crumbs">
            {here.group && <span className="crumb">{here.group}</span>}
            <h1 id="page-title">{here.label}</h1>
          </div>
          <div className="sp" />
          <button
            className="iconbtn"
            onClick={theme.toggle}
            title={theme.dark ? "Light theme" : "Dark theme"}
            aria-label={theme.dark ? "Light theme" : "Dark theme"}
          >
            <Icon name={theme.dark ? "sun" : "moon"} size={16} />
          </button>
        </header>

        <div className="content">
          {/* one broken panel must not blank the whole console */}
          <ErrorBoundary where={location.pathname}>
          <Routes>
            <Route path="/" element={<Today />} />
            <Route path="/redial-traffic" element={<RedialTrafficPage />} />
            <Route path="/redialing" element={<Redialing />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/audiences" element={<Audiences />} />
            <Route path="/blasts" element={<Blasts />} />
            <Route path="/blasts/new" element={<BlastNew />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/new" element={<CampaignNew />} />
            <Route path="/campaigns/:id/edit" element={<CampaignNew />} />
            <Route path="/journeys" element={<Journeys />} />
            <Route path="/queue" element={<Queue />} />
            <Route path="/replies" element={<Replies />} />
            <Route path="/copy" element={<Copy />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/cohorts" element={<Cohorts />} />
            <Route path="/dialing" element={<Dialing />} />
            <Route path="/traffic" element={<TrafficPage />} />
            <Route path="/autopilot" element={<Autopilot me={me} />} />
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/health" element={<Health />} />
            <Route
              path="/activity"
              element={
                me.can_see_activity ? (
                  <Activity />
                ) : (
                  <div className="empty">The activity log is not available to your account.</div>
                )
              }
            />
            <Route
              path="/keys"
              element={
                me.can_manage_keys ? (
                  <Keys />
                ) : (
                  <div className="empty">Managing keys is not available to your account.</div>
                )
              }
            />
            <Route path="/import" element={<Import />} />
            <Route path="/fields" element={<Fields />} />
            <Route path="/settings" element={<Settings me={me} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
